import { useEffect, useState, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Text,
  Stack,
  Group,
  ActionIcon,
  Tooltip,
  Loader,
  ScrollArea,
  Collapse,
  Paper,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronRight,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAgentStore } from '@/stores/agentStore';
import { fillingApi } from '@/services/templates.api';
import type { FilledField, DocumentFilling } from '@/types/template.types';
import type { FieldSuggestion } from '@/types/agent.types';

interface FieldsPanelProps {
  roomCode: string;
}

// Field states for border colors
type FieldState = 'pending' | 'has_suggestion' | 'filled' | 'has_improvement';

// Border colors for each state
const BORDER_COLORS: Record<FieldState, string> = {
  pending: '#fa5252',      // Red - Campo pendente
  has_suggestion: '#228be6', // Blue - Com sugestão
  filled: '#40c057',       // Green - Preenchido
  has_improvement: '#fab005', // Yellow - Com melhoria
};

// Combined field data for display
interface FieldDisplay {
  field: FilledField;
  state: FieldState;
  suggestion?: FieldSuggestion;
  displayValue?: string;
}

// Determine the state of a field
function getFieldState(
  field: FilledField,
  suggestion?: FieldSuggestion
): FieldState {
  const hasValue = field.value && field.value.trim().length > 0;
  const hasSuggestion = !!suggestion;

  if (hasValue && hasSuggestion) {
    return 'has_improvement';
  }
  if (hasValue) {
    return 'filled';
  }
  if (hasSuggestion) {
    return 'has_suggestion';
  }
  return 'pending';
}

// Sort fields: action-required first (blue/yellow), then by group and order
function sortFields(fields: FieldDisplay[]): FieldDisplay[] {
  const priority: Record<FieldState, number> = {
    has_suggestion: 0,
    has_improvement: 1,
    pending: 2,
    filled: 3,
  };

  return [...fields].sort((a, b) => {
    // First by action priority
    const priorityDiff = priority[a.state] - priority[b.state];
    if (priorityDiff !== 0) return priorityDiff;

    // Then by group name
    const groupCompare = a.field.groupName.localeCompare(b.field.groupName);
    if (groupCompare !== 0) return groupCompare;

    // Finally by field order
    return (a.field.fieldId || 0) - (b.field.fieldId || 0);
  });
}

// Individual field card component
interface FieldCardProps {
  fieldDisplay: FieldDisplay;
  onApprove: (fieldId: number, suggestionId: string) => Promise<void>;
  onReject: (fieldId: number, suggestionId: string) => Promise<void>;
  isProcessing: boolean;
}

const FieldCard = memo(function FieldCard({
  fieldDisplay,
  onApprove,
  onReject,
  isProcessing,
}: FieldCardProps) {
  const { field, state, suggestion, displayValue } = fieldDisplay;
  const borderColor = BORDER_COLORS[state];

  const handleApprove = useCallback(async () => {
    if (suggestion && field.fieldId) {
      await onApprove(field.fieldId, suggestion.suggestionId);
    }
  }, [suggestion, field.fieldId, onApprove]);

  const handleReject = useCallback(async () => {
    if (suggestion && field.fieldId) {
      await onReject(field.fieldId, suggestion.suggestionId);
    }
  }, [suggestion, field.fieldId, onReject]);

  return (
    <Paper
      p="sm"
      withBorder
      style={{
        borderLeft: `4px solid ${borderColor}`,
        borderLeftWidth: 4,
      }}
    >
      <Stack gap="xs">
        {/* Field name */}
        <Text fw={600} size="sm">
          {field.displayName}
          {field.isRequired && (
            <Text component="span" c="red" ml={4}>
              *
            </Text>
          )}
        </Text>

        {/* LLM hint / description */}
        {field.llmPromptHint && (
          <Text size="xs" c="dimmed" fs="italic">
            {field.llmPromptHint}
          </Text>
        )}

        {/* Current value (if filled) */}
        {displayValue && state !== 'has_suggestion' && (
          <Box
            p="xs"
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: 4,
            }}
          >
            <Text size="xs" c="dimmed" mb={2}>
              {state === 'has_improvement' ? 'Valor atual:' : 'Valor:'}
            </Text>
            <Text size="sm">{displayValue}</Text>
          </Box>
        )}

        {/* Suggestion (if has_suggestion or has_improvement) */}
        {suggestion && (
          <Box
            p="xs"
            style={{
              backgroundColor: state === 'has_improvement' ? '#fff9db' : '#e7f5ff',
              borderRadius: 4,
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Box style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" mb={2}>
                  {state === 'has_improvement' ? 'Sugestão melhor:' : 'Valor sugerido:'}
                </Text>
                <Text size="sm">{suggestion.proposedValue}</Text>
              </Box>
              <Group gap={4} wrap="nowrap">
                <Tooltip label="Aceitar" withArrow>
                  <ActionIcon
                    color="green"
                    variant="light"
                    size="sm"
                    onClick={handleApprove}
                    disabled={isProcessing}
                  >
                    {isProcessing ? <Loader size={14} /> : <IconCheck size={14} />}
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Rejeitar" withArrow>
                  <ActionIcon
                    color="red"
                    variant="light"
                    size="sm"
                    onClick={handleReject}
                    disabled={isProcessing}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Box>
        )}
      </Stack>
    </Paper>
  );
});

// Group header component
interface GroupHeaderProps {
  groupName: string;
  fieldCount: number;
  actionCount: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const GroupHeader = memo(function GroupHeader({
  groupName,
  fieldCount,
  actionCount,
  isExpanded,
  onToggle,
}: GroupHeaderProps) {
  return (
    <Group
      gap="xs"
      onClick={onToggle}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      py="xs"
    >
      {isExpanded ? (
        <IconChevronDown size={16} />
      ) : (
        <IconChevronRight size={16} />
      )}
      <Text fw={600} size="sm">
        {groupName}
      </Text>
      <Text size="xs" c="dimmed">
        ({fieldCount} campos)
      </Text>
      {actionCount > 0 && (
        <Text size="xs" c="blue" fw={500}>
          {actionCount} pendente{actionCount > 1 ? 's' : ''}
        </Text>
      )}
    </Group>
  );
});

export const FieldsPanel = memo(function FieldsPanel({ roomCode }: FieldsPanelProps) {
  const [filling, setFilling] = useState<DocumentFilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Get pending suggestions from store
  const pendingSuggestions = useAgentStore((state) => state.pendingSuggestions);
  const documentFields = useAgentStore((state) => state.documentFields);
  const removeSuggestion = useAgentStore((state) => state.removeSuggestion);
  const updateDocumentField = useAgentStore((state) => state.updateDocumentField);

  // Load filling data
  const loadFilling = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fillingApi.getByRoomCode(roomCode);
      setFilling(data);

      // Expand all groups by default
      const groups = new Set(data.fields.map((f) => f.groupName));
      setExpandedGroups(groups);
    } catch (err) {
      console.error('Failed to load filling:', err);
      setError('Erro ao carregar campos do documento');
    } finally {
      setLoading(false);
    }
  }, [roomCode]);

  useEffect(() => {
    loadFilling();
  }, [loadFilling]);

  // Combine filling fields with suggestions and store data
  const fieldsDisplay = useMemo((): FieldDisplay[] => {
    if (!filling) return [];

    return filling.fields.map((field) => {
      // Check for pending suggestion
      const suggestion = pendingSuggestions.find(
        (s) => s.fieldName === field.fieldName
      );

      // Check for approved value in store (might be newer than filling data)
      const storeField = documentFields.find(
        (f) => f.field_name === field.fieldName
      );

      // Determine current value (store takes precedence)
      const currentValue = storeField?.value || field.value;

      // Create updated field with store value
      const updatedField: FilledField = {
        ...field,
        value: currentValue || undefined,
      };

      const state = getFieldState(updatedField, suggestion);

      return {
        field: updatedField,
        state,
        suggestion,
        displayValue: currentValue || undefined,
      };
    });
  }, [filling, pendingSuggestions, documentFields]);

  // Sort fields
  const sortedFields = useMemo(
    () => sortFields(fieldsDisplay),
    [fieldsDisplay]
  );

  // Group fields by groupName
  const groupedFields = useMemo(() => {
    const groups = new Map<string, FieldDisplay[]>();

    for (const field of sortedFields) {
      const groupName = field.field.groupName;
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(field);
    }

    return groups;
  }, [sortedFields]);

  // Handle approve suggestion
  const handleApprove = useCallback(
    async (fieldId: number, suggestionId: string) => {
      setProcessingIds((prev) => new Set(prev).add(suggestionId));

      try {
        // Find the suggestion to get field info first
        const suggestion = pendingSuggestions.find(
          (s) => s.suggestionId === suggestionId
        );

        // Use fillingApi with fieldId and pass the proposed value explicitly
        // This ensures the value displayed to the user is what gets saved
        await fillingApi.approveField(roomCode, fieldId, {
          value: suggestion?.proposedValue,
        });

        if (suggestion) {
          // Update document field in store
          updateDocumentField({
            field_id: fieldId,
            field_name: suggestion.fieldName,
            value: suggestion.proposedValue,
            confidence: suggestion.confidence,
            extracted_at: new Date().toISOString(),
          });

          // Remove suggestion from store
          removeSuggestion(suggestionId);
        }

        notifications.show({
          title: 'Sugestão aceita',
          message: 'O valor foi aplicado ao campo',
          color: 'green',
        });

        // Reload filling to get updated data
        await loadFilling();
      } catch (err) {
        console.error('Failed to approve suggestion:', err);
        notifications.show({
          title: 'Erro',
          message: 'Não foi possível aceitar a sugestão',
          color: 'red',
        });
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    },
    [roomCode, pendingSuggestions, removeSuggestion, updateDocumentField, loadFilling]
  );

  // Handle reject suggestion
  const handleReject = useCallback(
    async (fieldId: number, suggestionId: string) => {
      setProcessingIds((prev) => new Set(prev).add(suggestionId));

      try {
        // Use fillingApi with fieldId for reliable rejection
        await fillingApi.rejectField(roomCode, fieldId);

        // Remove suggestion from store
        removeSuggestion(suggestionId);

        notifications.show({
          title: 'Sugestão rejeitada',
          message: 'A sugestão foi descartada',
          color: 'gray',
        });
      } catch (err) {
        console.error('Failed to reject suggestion:', err);
        notifications.show({
          title: 'Erro',
          message: 'Não foi possível rejeitar a sugestão',
          color: 'red',
        });
      } finally {
        setProcessingIds((prev) => {
          const next = new Set(prev);
          next.delete(suggestionId);
          return next;
        });
      }
    },
    [roomCode, removeSuggestion]
  );

  // Toggle group expansion
  const toggleGroup = useCallback((groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed" mt="xs">
          Carregando campos...
        </Text>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Text size="sm" c="red">
          {error}
        </Text>
      </Box>
    );
  }

  // Empty state
  if (!filling || filling.fields.length === 0) {
    return (
      <Box p="md" style={{ textAlign: 'center' }}>
        <Text size="sm" c="dimmed">
          Nenhum campo disponível
        </Text>
      </Box>
    );
  }

  // Count actions pending per group
  const getGroupActionCount = (fields: FieldDisplay[]) =>
    fields.filter(
      (f) => f.state === 'has_suggestion' || f.state === 'has_improvement'
    ).length;

  // Summary stats
  const totalFields = fieldsDisplay.length;
  const filledCount = fieldsDisplay.filter((f) => f.state === 'filled').length;
  const actionCount = fieldsDisplay.filter(
    (f) => f.state === 'has_suggestion' || f.state === 'has_improvement'
  ).length;
  const pendingCount = fieldsDisplay.filter((f) => f.state === 'pending').length;

  return (
    <Box h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Summary header */}
      <Box px="md" py="sm" style={{ borderBottom: '1px solid #e9ecef' }}>
        <Group gap="md">
          <Group gap={4}>
            <Box
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                backgroundColor: BORDER_COLORS.filled,
              }}
            />
            <Text size="xs">{filledCount} preenchidos</Text>
          </Group>
          {actionCount > 0 && (
            <Group gap={4}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 2,
                  backgroundColor: BORDER_COLORS.has_suggestion,
                }}
              />
              <Text size="xs" fw={500}>
                {actionCount} pendente{actionCount > 1 ? 's' : ''}
              </Text>
            </Group>
          )}
          <Group gap={4}>
            <Box
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                backgroundColor: BORDER_COLORS.pending,
              }}
            />
            <Text size="xs">{pendingCount} vazios</Text>
          </Group>
          <Text size="xs" c="dimmed" ml="auto">
            {totalFields} campos
          </Text>
        </Group>
      </Box>

      {/* Fields list */}
      <ScrollArea style={{ flex: 1 }} px="md" py="sm">
        <Stack gap="md">
          {Array.from(groupedFields.entries()).map(([groupName, fields]) => (
            <Box key={groupName}>
              <GroupHeader
                groupName={groupName}
                fieldCount={fields.length}
                actionCount={getGroupActionCount(fields)}
                isExpanded={expandedGroups.has(groupName)}
                onToggle={() => toggleGroup(groupName)}
              />
              <Collapse in={expandedGroups.has(groupName)}>
                <Stack gap="xs" pl="md">
                  {fields.map((fieldDisplay) => (
                    <FieldCard
                      key={fieldDisplay.field.fieldId}
                      fieldDisplay={fieldDisplay}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      isProcessing={
                        fieldDisplay.suggestion
                          ? processingIds.has(fieldDisplay.suggestion.suggestionId)
                          : false
                      }
                    />
                  ))}
                </Stack>
              </Collapse>
            </Box>
          ))}
        </Stack>
      </ScrollArea>
    </Box>
  );
});

export default FieldsPanel;
